import { Alert, Button, ModalV2 } from '@components'
import { useBusinessContext } from '@context'
import { IDocument } from '@query/documents'
import { useQueryClient } from '@tanstack/react-query'
import { formatCurrency } from '@utils'
import type { TFunction } from 'i18next'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalContent, ModalFooter } from './RelationMapper.styled'
import { RelationDocumentPicker } from './RelationDocumentPicker'
import { DocumentRelationFormValue } from './RelationRow'
import {
  buildPickerDocumentFromRelation,
  getCounterpartAccountFilters,
  getRelationsForLinkKind,
  hasRelationSelectionChanges,
  IRelationLinkKindMeta,
  RelationLinkKind
} from './relationMapperUtils'

interface RelationSelectionModalProps {
  isOpen: boolean
  linkKind: RelationLinkKind
  selection: number[]
  initialSelection: number[]
  onSelectionChange: (ids: number[]) => void
  onConfirm: () => void
  onClose: () => void
  documentId?: number
  activeRelations: DocumentRelationFormValue[]
  targetAmount: number | null
  preferredBalanceSign?: 1 | -1 | null
  linkKindMetas?: IRelationLinkKindMeta[] | null
}

interface RelationSelectionModalContext {
  header: string
  title: string
  description: string
  selectorLabel: string
  selectorInfo: string
  confirm: string
  selectionSummaryLabel: (count: number) => string
}

function buildRelationSelectionModalContext(
  pickerTarget: 'payments' | 'invoices',
  linkKind: RelationLinkKind,
  selectionCount: number,
  targetAmount: number | null,
  t: TFunction
): RelationSelectionModalContext {
  if (pickerTarget === 'payments') {
    return {
      header: t(`documentRelations.modal.header.${linkKind}`),
      title: t(`documentRelations.modal.context.${linkKind}.title`),
      description: t(`documentRelations.modal.context.${linkKind}.description`, {
        amount: formatCurrency(targetAmount ?? 0)
      }),
      selectorLabel: t(`documentRelations.modal.context.${linkKind}.selectorLabel`),
      selectorInfo: t('documentRelations.modal.context.accrual.selectorInfo'),
      confirm: t('documentRelations.modal.confirm.payments', { count: selectionCount }),
      selectionSummaryLabel: count =>
        t('documentRelations.modal.selectionCount.payments', { count })
    }
  }

  if (linkKind === 'salaryPayment') {
    return {
      header: t('documentRelations.modal.header.salaryPayment'),
      title: t('documentRelations.modal.context.salaryPayment.title'),
      description: t('documentRelations.modal.context.salaryPayment.description'),
      selectorLabel: t('documentRelations.modal.context.salaryPayment.selectorLabel'),
      selectorInfo: t('documentRelations.modal.context.salaryPayment.selectorInfo'),
      confirm: t('documentRelations.modal.confirm.salaries', { count: selectionCount }),
      selectionSummaryLabel: count =>
        t('documentRelations.modal.selectionCount.salaries', { count })
    }
  }

  return {
    header: t('documentRelations.modal.header.settlement'),
    title: t('documentRelations.modal.context.settlement.title'),
    description: t('documentRelations.modal.context.settlement.description'),
    selectorLabel: t('documentRelations.modal.context.settlement.selectorLabel'),
    selectorInfo: t('documentRelations.modal.context.settlement.selectorInfo'),
    confirm: t('documentRelations.modal.confirm.invoices', { count: selectionCount }),
    selectionSummaryLabel: count => t('documentRelations.modal.selectionCount.invoices', { count })
  }
}

export const RelationSelectionModal: React.FC<RelationSelectionModalProps> = ({
  isOpen,
  linkKind,
  selection,
  initialSelection,
  onSelectionChange,
  onConfirm,
  onClose,
  documentId,
  activeRelations,
  targetAmount,
  preferredBalanceSign = null,
  linkKindMetas = null
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()

  const excludeIds = typeof documentId === 'number' ? [documentId] : []
  const { accountTypes, match } = getCounterpartAccountFilters(linkKind, linkKindMetas)
  const pickerTarget =
    linkKind === 'payment' || linkKind === 'salaryPayment' ? 'invoices' : 'payments'
  const hasSelectionChanges = hasRelationSelectionChanges(selection, initialSelection)

  const pinnedDocuments = useMemo(
    () =>
      getRelationsForLinkKind(activeRelations, linkKind, linkKindMetas)
        .map(relation => {
          const cached = queryClient.getQueryData<IDocument>([
            slug,
            'documents',
            relation.related_document
          ])
          return cached ?? buildPickerDocumentFromRelation(relation)
        })
        .filter((document): document is IDocument => document != null),
    [activeRelations, linkKind, linkKindMetas, queryClient, slug]
  )

  const modalContext = buildRelationSelectionModalContext(
    pickerTarget,
    linkKind,
    selection.length,
    targetAmount,
    t
  )

  return (
    <ModalV2
      isVisible={isOpen}
      width={520}
      height={720}
      handleOnClose={onClose}
      header={modalContext.header}
      footer={
        <ModalFooter>
          <Button type="button" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            intent="success"
            onClick={onConfirm}
            disabled={!hasSelectionChanges}
          >
            {modalContext.confirm}
          </Button>
        </ModalFooter>
      }
    >
      <ModalContent>
        <Alert type="info" title={modalContext.title} description={modalContext.description} />

        <RelationDocumentPicker
          label={modalContext.selectorLabel}
          searchHint={modalContext.selectorInfo}
          value={selection}
          onChange={onSelectionChange}
          excludeIds={excludeIds}
          enabled={isOpen}
          targetAmount={targetAmount}
          accountTypes={accountTypes}
          accountTypeMatch={match}
          preferredBalanceSign={preferredBalanceSign}
          pinnedDocuments={pinnedDocuments}
          selectionSummaryLabel={modalContext.selectionSummaryLabel}
        />
      </ModalContent>
    </ModalV2>
  )
}
